/**
 * Test Mode
 *
 * Test mode is the widget running an agent profile's latest saved version
 * against the GLaDOS tester API instead of Chat Rocket, on the customer's own
 * website. It is a boot-time decision, never a mid-session swap: entering or
 * exiting persists this state and reloads the page, and the transport is chosen
 * once, before the auth machine runs.
 *
 * The state is persisted through the store's app-scoped envelope, so a page
 * load — a manual reload, or a `navigateToUrl` tool call mid-conversation —
 * restores test mode instead of booting the production transport into a test
 * conversation that does not exist there.
 *
 * The widget cannot reach the tester API from a customer page on its own (no
 * author credentials, no CORS grant), so a driver — the tester extension —
 * relays its requests over a private MessagePort. This module owns that wire
 * protocol's message shapes; the channel that speaks it lives in
 * `gateways/glados-tester`.
 */

/**
 * How the widget reaches the tester API.
 *
 * - `channel`: relayed by a driver over a private MessagePort. The only option
 *   on a customer page, where the widget has neither author credentials nor a
 *   CORS grant of its own.
 * - `direct`: the widget fetches the tester API itself, with whatever origin a
 *   relative path resolves to. That is only useful when the page is served by
 *   the dev server, and actively wrong once the widget is injected into a
 *   customer page — the extension loads it cross-origin but it executes in the
 *   host page's realm, so a relative fetch would resolve against the customer's
 *   origin. Dev builds only, enforced in `validTestModeNetwork`.
 */
export const TEST_MODE_NETWORKS = ['channel', 'direct'] as const;

export type TestModeNetwork = (typeof TEST_MODE_NETWORKS)[number];

export interface TestModeState {
  /** Whether this widget boots against the tester API. */
  active: boolean;
  /** When test mode was entered. The TTL is measured from here. */
  enteredAt?: number;
  network?: TestModeNetwork;
  /** Org whose agent profile is under test. */
  orgId?: string;
  /** Agent profile under test, always its latest saved version. */
  profileId?: string;
  /** Display name of the profile, for the in-widget banner. */
  profileName?: string;
  /** The GLaDOS test session in progress, resumed across page loads. */
  sessionId?: string;
}

/**
 * The configuration needed to enter test mode, as supplied by a driver through
 * the Test Mode API. A subset of {@link TestModeState}: the session and the
 * entry timestamp are the widget's to assign.
 */
export interface TestModeConfig {
  network?: TestModeNetwork;
  orgId: string;
  profileId: string;
  profileName?: string;
}

/**
 * How long a `profileName` may be.
 *
 * Exported because it is a boundary the driver has to agree with: a driver that
 * accepts a longer name forwards one this validator then rejects, and the author
 * sees the whole enter call fail over a display string. Derive from this rather
 * than restating the number.
 */
export const MAX_PROFILE_NAME_LENGTH = 200;

export function getInitialTestModeState(): TestModeState {
  return { active: false };
}

/**
 * How long an entered test mode stays valid. Test mode is persisted and
 * origin-scoped, so without an expiry an author who tests a site once and never
 * toggles off would leave that origin pinned to the tester API indefinitely —
 * across browser restarts, on every page of the site.
 */
export const TEST_MODE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Whether a persisted test mode has aged out and should boot as production.
 *
 * An active state whose `enteredAt` cannot be trusted is treated as expired,
 * because that field is the whole of the TTL's enforcement. A missing or
 * non-numeric stamp qualifies, and so does one dated into the future: `now - enteredAt` is
 * negative there, so a forward-dated blob — and localStorage is host-page
 * writable — would otherwise never age out at all.
 *
 * The future check is strict rather than skew-tolerant. The stamp is written and
 * read by one machine's clock, so the only way past `now` is the clock itself
 * moving back, and the cost of that is an author re-toggling test mode.
 */
export function isTestModeExpired(state: TestModeState, now: number = Date.now()): boolean {
  if (!state.active) {
    return false;
  }
  if (typeof state.enteredAt !== 'number' || !Number.isFinite(state.enteredAt)) {
    return true;
  }
  if (state.enteredAt > now) {
    return true;
  }
  return now - state.enteredAt > TEST_MODE_TTL_MS;
}

/**
 * Whether this widget should boot against the tester API — the single question
 * the boot decision needs answered.
 *
 * It exists so no caller has to remember to pair the two checks: asking only
 * `!isTestModeExpired(state)` is true for a widget that was never in test mode,
 * which would build a tester gateway on a production site and leave the shopper
 * with a chat that never answers.
 */
export function isTestModeUsable(state: TestModeState, now: number = Date.now()): boolean {
  return state.active && !isTestModeExpired(state, now);
}

/**
 * Tester Network Channel protocol.
 *
 * Bumped only on a breaking change: unknown `kind`s are ignored on both sides,
 * so the protocol grows additively. A driver ships a supported range and, on a
 * mismatch, tells the author to update — through its own UI, not over this
 * channel, since a driver that cannot speak the version cannot be trusted to
 * frame a message in it either.
 */
export const TESTER_PROTOCOL_VERSION = 1;

export const TESTER_REQUEST_METHODS = ['GET', 'POST'] as const;

export type TesterRequestMethod = (typeof TESTER_REQUEST_METHODS)[number];

interface TesterMessageBase {
  /** Correlates a response or stream with the message that opened it. */
  id: string;
  /**
   * Protocol version. Narrowed to the version this build speaks, so emitting a
   * number nobody agreed on is a compile error rather than a runtime mismatch.
   */
  v: typeof TESTER_PROTOCOL_VERSION;
}

/**
 * Paths, never URLs: the driver owns the origin, so the widget cannot aim a
 * relayed request at a host of its choosing.
 */
export interface TesterRequestMessage extends TesterMessageBase {
  body?: unknown;
  kind: 'request';
  method: TesterRequestMethod;
  path: string;
}

export interface TesterStreamCloseMessage extends TesterMessageBase {
  kind: 'stream-close';
}

export interface TesterStreamOpenMessage extends TesterMessageBase {
  kind: 'stream-open';
  path: string;
}

export type TesterWidgetMessage = TesterRequestMessage | TesterStreamCloseMessage | TesterStreamOpenMessage;

export interface TesterResponseMessage extends TesterMessageBase {
  errorMessage?: string;
  json?: unknown;
  kind: 'response';
  ok: boolean;
  status: number;
}

/**
 * The stream this `id` opened has ended, with `errorMessage` set when it ended
 * badly — the tester API refused it, the connection dropped, the driver went
 * away. Without a terminator the three failures are indistinguishable from a
 * quiet conversation, and the widget waits on a stream that is never coming
 * back; the gateway needs to tell "nothing to say yet" from "nothing more is
 * coming" to decide between waiting and resubscribing.
 */
export interface TesterStreamEndMessage extends TesterMessageBase {
  errorMessage?: string;
  kind: 'stream-end';
}

/**
 * A server-sent event, forwarded verbatim. The driver parses the SSE wire
 * format but not the events themselves — only the widget knows what `changed`
 * or `typing` mean — which keeps new event types out of the driver's release
 * cycle.
 */
export interface TesterStreamEventMessage extends TesterMessageBase {
  data: string;
  kind: 'stream-event';
  name: string;
}

export type TesterDriverMessage = TesterResponseMessage | TesterStreamEndMessage | TesterStreamEventMessage;

/**
 * Test Mode API.
 *
 * The driver-agnostic surface a test driver — the extension, the console, a
 * Playwright script — drives test mode through, exposed on `window.Gladly` by
 * `bootstrap.ts`. The widget never references any particular driver.
 *
 * Both results are `null` before the widget has initialized, which a driver must
 * read as "not yet" rather than "not supported": it polls until one answers.
 */

/**
 * What `_getTestModeSupport()` reports. A driver needs all four *before* test
 * mode is entered, which is why `_getTestModeStatus()` cannot serve them — that
 * returns test-mode state, and there is none yet.
 *
 * - `orgId` is the org the driver lists agent profiles for. It is why an author
 *   never picks an org: the org is whichever site they are standing on.
 * - `gladlySite` is the cluster, a hint for the driver's host prompt rather than
 *   the host it calls — that is the org's own Gladly URL.
 * - `appId` distinguishes two widgets on one origin, so a driver can scope its
 *   per-site memory correctly.
 */
export interface TestModeSupport {
  /**
   * Narrowed to the version this build speaks, and deliberately the same number
   * as the wire protocol's `v`: a driver that cannot frame a message for this
   * widget cannot drive it either, so one skew check covers both.
   */
  apiVersion: typeof TESTER_PROTOCOL_VERSION;
  appId: string;
  gladlySite: string;
  orgId: string;
}

/**
 * What `_getTestModeStatus()` reports about the page load it is called on.
 *
 * `orgId` here is the org whose profile this page load is testing, read from the
 * persisted slice. It is not necessarily the org the site belongs to — that is
 * `TestModeSupport.orgId`, and a driver listing profiles should key off that
 * one. The two agree whenever the tester gateway was actually built, since
 * `createGladosTestGateway` cross-checks them and falls back to production on a
 * mismatch; they can differ on an origin whose widget has since moved org.
 *
 * `awaitingChannel`: this page load expects its transport from a driver and has
 * not received a port. False for a production boot and a `direct` boot.
 */
export interface TestModeStatus {
  active: boolean;
  awaitingChannel: boolean;
  orgId?: string;
  profileId?: string;
  sessionId?: string;
}

/**
 * Build the status a driver reads.
 *
 * The reported `active` is `bootedInTestMode`: whether this page load runs the
 * tester transport, as the caller settles it from the boot decision and the live
 * slice. `state.active` alone is not that — it is already true between
 * `_enterTestMode` and the reload it triggers, while the page still runs
 * production — and a status read once can turn false later in the page load,
 * when the widget bundle falls back to production and retires the slice.
 *
 * The identifying fields are reported only while active, so the answer describes
 * what this widget is testing rather than what some later page load might.
 */
export function toTestModeStatus(
  bootedInTestMode: boolean,
  state: TestModeState | null | undefined,
  channelHeld: boolean,
): TestModeStatus {
  if (!bootedInTestMode) {
    return { active: false, awaitingChannel: false };
  }
  const { network, orgId, profileId, sessionId } = state ?? {};
  return {
    active: true,
    // A `direct` boot fetches the tester API itself and has no port to wait for.
    awaitingChannel: network !== 'direct' && !channelHeld,
    ...(orgId !== undefined && { orgId }),
    ...(profileId !== undefined && { profileId }),
    ...(sessionId !== undefined && { sessionId }),
  };
}

/**
 * Whether a test-mode state names what a driver is asking for. Paired by the
 * caller with the boot decision, the two answer whether entering again would be
 * a no-op instead of another reload: a driver may re-signal — a toggle clicked
 * twice, a signal repeated on every page load — and reloading on each one would
 * leave the page in a loop.
 *
 * `network` counts towards identity even though a driver never sets it: a dev
 * moving between the relay and the direct fetch changes transports, and that
 * only takes effect on a reload.
 */
export function isTestingWith(state: TestModeState | null | undefined, config: TestModeConfig): boolean {
  return (
    state?.orgId === config.orgId &&
    state.profileId === config.profileId &&
    (state.network ?? 'channel') === (config.network ?? 'channel')
  );
}
